import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { AlternativeCar } from '../../components/ui/AlternativeCars.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { Input } from '../../components/ui/FormField.js';
import { useGapWarning } from '../../components/ui/GapWarning.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { freeForLabel, wallToInstant } from './freeFor.js';
import {
  type CollapsedState,
  type GroupMode,
  groupCars,
  readCollapsed,
  toggleCollapsed,
  writeCollapsed,
} from './grouping.js';
import { ScheduleGuide } from './guide/ScheduleGuide.js';
import { initialGuideOpen, writeGuideOpen } from './guide/guideState.js';
import { type ScheduleDensity, readDensity, writeDensity } from './scheduleDensity.js';
import {
  type ScheduleParams,
  filterBars,
  readScheduleParams,
  writeScheduleParams,
} from './scheduleParams.js';
import { BlockChangePopover } from './timeline/BlockChangePopover.js';
import { BlockDetailsPopover } from './timeline/BlockDetailsPopover.js';
import { BookingDetailsPopover } from './timeline/BookingDetailsPopover.js';
import { CreateBlockPopover } from './timeline/CreateBlockPopover.js';
import { MoveBookingPopover, type MoveState } from './timeline/MoveBookingPopover.js';
import { ResizeBookingPopover, type ResizePrice } from './timeline/ResizeBookingPopover.js';
import { shortRef } from './timeline/ScheduleBarCard.js';
import { ScheduleTimeline } from './timeline/ScheduleTimeline.js';
import { barStyleFor } from './timeline/barStyle.js';
import {
  type CreateProposal,
  type DragProposal,
  type ResizeProposal,
  type ScheduleView,
  mytDateOf,
  mytPeriod,
} from './timeline/dropLogic.js';
import { columnAvailability, occupancySummary } from './timeline/occupancy.js';
import { CarReorderPanel } from './toolbar/CarReorderPanel.js';
import { DisplayMenu } from './toolbar/DisplayMenu.js';
import { FreeForControl } from './toolbar/FreeForControl.js';
import { ScheduleToolbar } from './toolbar/ScheduleToolbar.js';
import type { CarChangeProposal, ProposedBlock, ScheduleBar, ScheduleResponse } from './types.js';

/**
 * 094 — the fleet schedule: one day, cars as rows.
 *
 * The initial day is NOT computed here. "Today" is a question about the schedule's zone,
 * and this panel does no timezone arithmetic (research D1) — a UTC date would be wrong for
 * eight hours of every day, opening the screen on yesterday. So the first request names no
 * day, the platform answers with its own, and every step from there is plain string
 * arithmetic on that answer.
 */
function shiftDay(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** 316: the 1st of the month `months` away from `day`'s month. */
function shiftMonth(day: string, months: number): string {
  const d = new Date(Date.parse(`${day}T00:00:00Z`));
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1))
    .toISOString()
    .slice(0, 10);
}

function stepDate(view: ScheduleView, day: string, direction: 1 | -1): string {
  if (view === 'week') return shiftDay(day, 7 * direction);
  if (view === 'month') return shiftMonth(day, direction);
  return shiftDay(day, direction);
}

/** 328 (#736): what `GET /bookings/:id/move-preview` answers (contracts/move.md). */
interface MovePreviewResponse {
  span: {
    from: { car_id: string; pickup_at: string; return_at: string };
    to: { car_id: string; pickup_at: string; return_at: string };
  } | null;
  legs: Array<{ booking_id: string }>;
  price: {
    mode: 'kept' | 'repriced';
    reason_required: boolean;
    currency: string;
    current_total_minor: number;
    new_total_minor: number | null;
  } | null;
  clash: {
    code: string;
    message: string;
    suggestion: { pickup_at: string; return_at: string } | null;
  } | null;
  /** 379: the move corrects a finished, paid rental (a reason is required; the customer is not told). */
  correction?: boolean;
  refusal: { code: string; message: string } | null;
}

function moneyOf(minor: number, currency: string): string {
  return `${currency === 'MYR' ? 'RM' : `${currency} `}${(minor / 100).toFixed(2)}`;
}

/** The popover's `move` state from the preview; `undefined` while it is still being asked. */
function moveStateOf(p: MovePreviewResponse | undefined): MoveState {
  if (!p) return { loading: true, reasonRequired: false };
  if (p.refusal) return { loading: false, reasonRequired: false, refusal: p.refusal.message };
  const price = p.price;
  const priceText = !price
    ? undefined
    : p.correction
      ? "This rental is over, so the move corrects its record: price stays as paid, the customer is not told, and the reason is recorded in the booking's history."
      : price.mode === 'repriced' && price.new_total_minor !== null
        ? `New price ${moneyOf(price.new_total_minor, price.currency)} (was ${moneyOf(price.current_total_minor, price.currency)})`
        : price.reason_required
          ? "Price stays as paid, and a car at another location is allowed. The reason is recorded in the booking's history."
          : 'Price unchanged — the rental keeps its length.';
  return {
    loading: false,
    reasonRequired: Boolean(price?.reason_required),
    priceText,
    silent: Boolean(p.correction),
  };
}

/**
 * 325 (#477): a car move takes the whole rental, and Change Car (which re-prices) is refused once any
 * leg is paid, so the path is chosen from the RENTAL's money: an unpaid extension of a paid original
 * goes to Correct Car. Falls back to the bar's own fact on a platform older than 325.
 */
function rentalPaid(bar: ScheduleBar): boolean {
  return Boolean(bar.rental_money_settled ?? bar.money_settled);
}

export function SchedulePage() {
  /**
   * 316 (US2): the view lives in the address, so a reload or a shared link opens the same view.
   * Day keeps the request it always sent (no `view` parameter); Week and Month name their view and
   * anchor date. Day still draws the old timeline until drag, create and reorder are ported
   * (T020–T026); Week and Month draw the new one, read only.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  // 316 (US6, FR-012): the address is the single source of what is shown — view, date and every
  // filter — so a reload or a shared link opens the same thing.
  const params = readScheduleParams(searchParams);
  const { view, statuses, location: locationId, freeFrom, freeTo } = params;
  const freeActive = !!freeFrom && !!freeTo;
  const day = params.date;
  const showUnavailable = params.unavailable;
  const setParams = (patch: Partial<ScheduleParams>) =>
    setSearchParams((prev) => writeScheduleParams({ ...readScheduleParams(prev), ...patch }), {
      replace: true,
    });
  // 316 (FR-015): the block whose details are open, and where its popover anchors.
  const [selected, setSelected] = useState<{ bar: ScheduleBar; anchorRect: DOMRect } | null>(null);
  const ability = useAbility();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // FR-018: a caller who cannot write is offered nothing to write with. The platform
  // refuses them anyway (contract 24) — this only stops the panel implying otherwise.
  const canEdit = ability.can('update', 'CarBlock') && ability.can('create', 'CarBlock');
  const canRemove = ability.can('delete', 'CarBlock');
  // 216 (#312, FR-010): on TOP of `canEdit` above — the schedule's own write gate says nothing
  // about whether this caller may change which car a BOOKING is on, which is a `Booking` ability.
  const canMoveBooking = ability.can('manage', 'Booking');
  // 301 (#437) US2: reordering the car COLUMN is a `Car` write (contracts/patch-cars-id-order.md),
  // a separate ability from `canEdit` above, which governs `CarBlock` only.
  const canReorderCars = ability.can('update', 'Car');

  /**
   * 158 (#4): narrow the timeline to one vehicle.
   *
   * 🔴 Deliberately independent of the day. This page's subject is a day, and 096 records a
   * defect where a proposal outlived a day change and would have been written onto the wrong
   * one. Filtering changes which cars are drawn and nothing else — the day parameter is built
   * exactly as it was.
   */
  const [plateInput, setPlateInput] = useState(params.plate);
  const plateQuery = params.plate;

  // biome-ignore lint/correctness/useExhaustiveDependencies: only the typed text starts the debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = plateInput.trim();
      if (trimmed !== plateQuery) setParams({ plate: trimmed });
    }, 400);
    return () => clearTimeout(timer);
  }, [plateInput]);

  // 316: the location list for the filter (the server filters by `location_id`).
  const { data: locationList } = useQuery({
    queryKey: ['schedule-locations'],
    queryFn: () => api.get<{ items: Array<{ _id: string; name: string }> }>('/locations?limit=100'),
    retry: false,
  });

  const schedulePath = (() => {
    const params = new URLSearchParams();
    if (view === 'day') {
      if (day) params.set('day', day);
    } else {
      params.set('view', view);
      if (day) params.set('date', day);
    }
    if (plateQuery) params.set('plate_number', plateQuery);
    if (locationId) params.set('location_id', locationId);
    // 301 (#437) US1: an absent param, not a `false` one, when off — the request shape a fresh page
    // load sends is identical to what this page always sent.
    if (showUnavailable) params.set('show_unavailable', 'true');
    // 320 (FR-001): the free-for window, sent as instants (the address keeps the wall clock).
    if (freeFrom && freeTo) {
      params.set('free_from', wallToInstant(freeFrom));
      params.set('free_to', wallToInstant(freeTo));
    }
    const query = params.toString();
    return query ? `/schedule?${query}` : '/schedule';
  })();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'schedule',
      view,
      day ?? 'today',
      plateQuery,
      locationId,
      showUnavailable,
      freeFrom,
      freeTo,
    ],
    queryFn: () => api.get<ScheduleResponse>(schedulePath),
    // A refusal (413) is a statement, not a transient fault — retrying it only delays it.
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['schedule'] });
  const [writeError, setWriteError] = useState<string | null>(null);
  // 096: what the operator has drawn but not yet confirmed. Nothing is written while this is set.
  const [proposal, setProposal] = useState<ProposedBlock | null>(null);

  /**
   * 🔴 A proposal belongs to the day it was drawn on.
   *
   * Found at the completion gate (T049) by re-reading the spec's edge cases. Changing the day
   * makes `data` briefly undefined, which unmounts the popover — but it came straight back when
   * the refetch settled, still holding clock times from the OLD day while `windowFrom` was now the
   * NEW one. Confirming would have written the block onto a day the operator never dragged on.
   *
   * Keyed on the day the proposal was made against rather than a `useEffect` on `day`, so there is
   * no window in which a stale proposal is reachable at all.
   */
  const proposalDay = useRef<string | null>(null);
  const visibleDay = data?.window.day ?? null;
  const liveProposal = proposal && proposalDay.current === visibleDay ? proposal : null;
  const onWriteError = (err: unknown) => setWriteError((err as Error).message);

  const [proposalError, setProposalError] = useState<string | null>(null);

  const createBlock = useMutation({
    // 096: the whole confirmed proposal, not the dragged range. `type` and `reason` were always
    // accepted by this endpoint — the admin simply never sent them, so every block made here was
    // a generic one with no note.
    mutationFn: (v: ProposedBlock) =>
      api.post(`/cars/${v.carId}/blocks`, {
        type: v.type,
        start_at: v.startAt.toISOString(),
        end_at: v.endAt.toISOString(),
        // Omitted entirely when empty — never sent as an empty string.
        ...(v.reason ? { reason: v.reason } : {}),
      }),
    onSuccess: () => {
      setWriteError(null);
      setProposalError(null);
      // Only now does the proposal go away. Closing it on submit would throw away a refusal the
      // operator still needs to see.
      setProposal(null);
      invalidate();
    },
    // 096: a refusal stays with the popover, which stays open, so the range can be corrected.
    onError: (err: unknown) => setProposalError((err as Error).message),
  });

  const patchBlock = useMutation({
    mutationFn: (v: { carId: string; blockId: string; body: Record<string, string> }) =>
      api.patch(`/cars/${v.carId}/blocks/${v.blockId}`, v.body),
    onSuccess: () => {
      setWriteError(null);
      invalidate();
    },
    onError: onWriteError,
  });

  const removeBlock = useMutation({
    mutationFn: (v: { carId: string; blockId: string }) =>
      api.delete(`/cars/${v.carId}/blocks/${v.blockId}`),
    onSuccess: () => {
      setWriteError(null);
      setSelected(null);
      invalidate();
    },
    onError: onWriteError,
  });

  // 301 (#437) US2: onSuccess re-fetches the schedule so the car column reflects the new,
  // persisted order immediately — a failed reorder leaves the car exactly where it already was
  // (nothing here holds any OPTIMISTIC local order for `Timeline` to have moved it to).
  const reorderCar = useMutation({
    mutationFn: (v: { carId: string; afterCarId: string | null }) =>
      api.patch(`/cars/${v.carId}/order`, { after_car_id: v.afterCarId }),
    onSuccess: () => {
      setWriteError(null);
      invalidate();
    },
    onError: onWriteError,
  });

  const barCar = (bar: ScheduleBar) => bar.car_id;

  /**
   * 316 (US1) — a booking dragged on the new timeline, waiting for Confirm. Nothing is written
   * while this is set (FR-004).
   *
   * 🔴 Bound to the view AND date it was made in, the same way 096 binds a drawn block to its day:
   * after a change of period the popover is gone and the proposal cannot be saved (spec edge case).
   */
  const periodKey = data ? `${view}|${data.window.day}` : null;
  const [move, setMove] = useState<{
    proposal: DragProposal;
    anchorRect: DOMRect;
    key: string;
  } | null>(null);
  const liveMove = move && move.key === periodKey ? move : null;
  const [moveError, setMoveError] = useState<string | null>(null);
  const [moveAlternatives, setMoveAlternatives] = useState<AlternativeCar[]>([]);
  const moveCar = liveMove?.proposal.kind === 'car' ? liveMove.proposal : null;
  // FR-006: a booking with settled money keeps its price, so it is never quoted.
  const moveNeedsQuote = Boolean(moveCar && !rentalPaid(moveCar.bar));
  const { data: moveQuote } = useQuery({
    queryKey: ['schedule-move-quote', moveCar?.bar.id, moveCar?.targetCarId],
    queryFn: () =>
      api.post<{
        outcome: string;
        pricing_template_id?: string;
        currency_symbol?: string;
        grand_total_minor?: number;
        total_minor?: number;
      }>('/pricing/quote', {
        car_id: moveCar?.targetCarId,
        pickup_at: moveCar?.bar.start_at,
        return_at: moveCar?.bar.end_at,
      }),
    enabled: moveNeedsQuote,
  });
  // 328 (#736): a `move` (a target car and a time shift) is answered by the SERVER's move preview —
  // the span from and to, what happens to the price, whether a reason is needed, and any refusal.
  const moveMove = liveMove?.proposal.kind === 'move' ? liveMove.proposal : null;
  const suggestWindow = data ? { view, from: data.window.from, to: data.window.to } : null;
  const { data: movePreview, isFetching: movePreviewLoading } = useQuery({
    queryKey: [
      'schedule-move-preview',
      moveMove?.bar.booking_id,
      moveMove?.targetCarId,
      moveMove?.deltaMs,
      suggestWindow?.from,
      suggestWindow?.to,
    ],
    queryFn: () => {
      const q = new URLSearchParams({
        car_id: moveMove?.targetCarId ?? '',
        shift_ms: String(moveMove?.deltaMs ?? 0),
        ...(suggestWindow ?? {}),
      });
      return api.get<MovePreviewResponse>(
        `/bookings/${moveMove?.bar.booking_id}/move-preview?${q.toString()}`,
      );
    },
    enabled: Boolean(moveMove?.bar.booking_id && suggestWindow),
  });
  // 328 US2: a clash the server found only on save (after the preview) — its message and suggestion.
  const [moveSaveClash, setMoveSaveClash] = useState<{
    message: string;
    suggestion: { pickup_at: string; return_at: string } | null;
  } | null>(null);
  const closeMove = () => {
    setMove(null);
    setMoveError(null);
    setMoveAlternatives([]);
    setMoveSaveClash(null);
  };
  const saveMove = useSaveMutation<{ url: string; body: Record<string, unknown> }>({
    resource: 'booking',
    affects: ['schedule'],
    mutationFn: ({ url, body }) => api.patch(url, body),
    onSaved: closeMove,
    onError: (err) => {
      const e = err as { code?: string; details?: { suggestion?: unknown } } & Error;
      // 328 US2: a clash on save offers the server's suggested time instead of a dead end.
      if (
        (e.code === 'SLOT_TAKEN' || e.code === 'CAR_BLOCKED') &&
        e.details &&
        'suggestion' in e.details
      ) {
        setMoveSaveClash({
          message: e.message,
          suggestion:
            (e.details.suggestion as { pickup_at: string; return_at: string } | null) ?? null,
        });
        return;
      }
      setMoveError(e.message || 'The booking could not be moved.');
      setMoveAlternatives((err as { alternatives?: AlternativeCar[] }).alternatives ?? []);
    },
  });
  const carName = (carId: string) => {
    const car = data?.cars.find((c) => c.id === carId);
    return car ? `${car.label} · ${car.plate_number}` : 'another car';
  };
  const confirmMove = (reason?: string) => {
    if (!liveMove) return;
    const p = liveMove.proposal;
    const id = p.bar.booking_id;
    if (!id) return;
    if (p.kind === 'car') {
      if (rentalPaid(p.bar)) {
        void saveMove.save({
          url: `/bookings/${id}/car/correction`,
          body: { car_id: p.targetCarId, reason: reason ?? '' },
        });
        return;
      }
      if (!moveQuote?.pricing_template_id) {
        setMoveError('No price could be resolved for this car.');
        return;
      }
      void saveMove.save({
        url: `/bookings/${id}/car`,
        body: { car_id: p.targetCarId, pricing_template_id: moveQuote.pricing_template_id },
      });
      return;
    }
    if (p.kind === 'move') saveMoveAt(p.deltaMs, reason);
  };
  const saveMoveAt = (shiftMs: number, reason?: string) => {
    const p = liveMove?.proposal;
    if (!p || p.kind !== 'move' || !p.bar.booking_id) return;
    setMoveSaveClash(null);
    void saveMove.save({
      url: `/bookings/${p.bar.booking_id}/move`,
      body: {
        car_id: p.targetCarId,
        shift_ms: shiftMs,
        ...(reason ? { reason } : {}),
        ...(suggestWindow ? { suggest: suggestWindow } : {}),
      },
    });
  };
  // 328 US2: the clash to show — one found on save wins over the preview's — and taking its suggestion,
  // which is a shift from the moving span's CURRENT start (the server's span when it answered).
  const shownClash =
    moveSaveClash ?? (!movePreviewLoading && movePreview?.clash ? movePreview.clash : null);
  const useSuggestion = (reason?: string) => {
    const p = liveMove?.proposal;
    if (!p || p.kind !== 'move' || !shownClash?.suggestion) return;
    const from = movePreview?.span
      ? Date.parse(movePreview.span.from.pickup_at)
      : Date.parse(p.bar.start_at);
    saveMoveAt(Date.parse(shownClash.suggestion.pickup_at) - from, reason);
  };
  const moveQuoteLabel =
    moveQuote?.outcome === 'priced'
      ? `${moveQuote.currency_symbol ?? ''}${(
          (moveQuote.grand_total_minor ?? moveQuote.total_minor ?? 0) / 100
        ).toFixed(2)}`
      : undefined;

  const goTo = (nextView: ScheduleView, nextDay: string | null) =>
    setParams({ view: nextView, date: nextDay });
  const visibleBars = data ? filterBars(data.bars, statuses) : [];
  // 317 (R1, R6, FR-013): the figures read the UNFILTERED bars, once per response — the status
  // filter changes what is drawn, never what is counted, and a drag never recomputes them.
  const occupancy = useMemo(
    () =>
      data
        ? occupancySummary(
            data.cars,
            data.bars,
            { start: Date.parse(data.window.from), end: Date.parse(data.window.to) },
            view,
          )
        : undefined,
    [data, view],
  );
  const [reordering, setReordering] = useState(false);
  // 329 (US1, FR-006): the Guide panel, and the button focus returns to when it closes. US4 (FR-010):
  // it opens by itself on this browser's first visit; after that, the last choice is remembered here.
  const [guideOpen, setGuideOpen] = useState(() => initialGuideOpen(browserStorage()));
  const guideButton = useRef<HTMLButtonElement>(null);
  const chooseGuide = (open: boolean) => {
    setGuideOpen(open);
    writeGuideOpen(browserStorage(), open);
  };
  // 318 (US2, R2): row density is this viewer's preference, kept in this browser and never in the
  // address; `browserStorage()` shields a refusing browser, where even the accessor can throw.
  const [density, setDensity] = useState<ScheduleDensity>(() => readDensity(browserStorage()));
  const chooseDensity = (next: ScheduleDensity) => {
    setDensity(next);
    writeDensity(browserStorage(), next);
  };
  // 319 (US2, FR-006a): which groups are collapsed — per viewer, per mode, kept in this browser.
  const [collapsed, setCollapsed] = useState<CollapsedState>(() => readCollapsed(browserStorage()));
  const groupMode = params.group;
  const toggleGroup = (key: string) => {
    if (groupMode === 'none') return;
    const next = toggleCollapsed(collapsed, groupMode, key);
    setCollapsed(next);
    writeCollapsed(browserStorage(), next);
  };
  // 319 (R2, R3): groups are the loaded cars gathered by location or category, each with its own
  // free cars per column by 317's rule. The unfiltered bars, like 317's figures.
  const groups = useMemo(() => {
    if (!data || groupMode === 'none') return null;
    const range = { start: Date.parse(data.window.from), end: Date.parse(data.window.to) };
    return (groupCars(data.cars, groupMode) ?? []).map((g) => ({
      ...g,
      columns: columnAvailability(g.cars, data.bars, range, view),
    }));
  }, [data, groupMode, view]);
  const anchorDay = data?.window.day ?? day;

  // #631: a click shows the bar's details beside it — a booking's card with **Open booking**, a
  // block's editable details — and the operator stays on the schedule. Until #631 a booking click
  // navigated away (094 FR-013); the booking is still one button away, and an extension still leads
  // to the SAME booking (094 FR-011a), because its bar's `booking_id` is already the booking's.
  // 396: a bar passes its own rectangle (a car drawn under two hosts has two); a day cell's list does not.
  const openBar = (bar: ScheduleBar, rect?: DOMRect) => {
    const el = rect
      ? null
      : document.querySelector<HTMLElement>(`[data-testid="schedule-bar-${bar.id}"]`);
    setSelected({ bar, anchorRect: rect ?? el?.getBoundingClientRect() ?? new DOMRect() });
  };
  const isBookingSelection =
    selected !== null && (selected.bar.kind === 'booking' || selected.bar.kind === 'extension');
  const editableSelection = selected && !isBookingSelection ? selected : null;
  const bookingSelection = selected && isBookingSelection ? selected : null;

  /**
   * 316 (US4, FR-015) — a block moved, resized or drawn on the new timeline, waiting for Confirm.
   * Bound to its period exactly like a booking move.
   */
  const [blockPlan, setBlockPlan] = useState<{
    proposal: Extract<DragProposal, { kind: 'block-move' }> | ResizeProposal | CreateProposal;
    anchorRect: DOMRect;
    key: string;
  } | null>(null);
  const liveBlockPlan = blockPlan && blockPlan.key === periodKey ? blockPlan : null;
  const [blockPlanError, setBlockPlanError] = useState<string | null>(null);
  const closeBlockPlan = () => {
    setBlockPlan(null);
    setBlockPlanError(null);
  };
  const saveBlockPlan = useSaveMutation<{
    method: 'patch' | 'post';
    url: string;
    body: Record<string, string>;
  }>({
    resource: 'schedule',
    mutationFn: ({ method, url, body }) =>
      method === 'post' ? api.post(url, body) : api.patch(url, body),
    onSaved: closeBlockPlan,
    onError: (err) => setBlockPlanError((err as Error).message || 'The block could not be saved.'),
  });
  const confirmBlockPlan = (extra: { type?: string; reason?: string }) => {
    if (!liveBlockPlan) return;
    const p = liveBlockPlan.proposal;
    if (p.kind === 'create') {
      void saveBlockPlan.save({
        method: 'post',
        url: `/cars/${p.carId}/blocks`,
        body: {
          type: extra.type ?? 'manual_block',
          start_at: new Date(p.start).toISOString(),
          end_at: new Date(p.end).toISOString(),
          ...(extra.reason ? { reason: extra.reason } : {}),
        },
      });
      return;
    }
    const [start, end] =
      p.kind === 'resize'
        ? [p.start, p.end]
        : [Date.parse(p.bar.start_at) + p.deltaMs, Date.parse(p.bar.end_at) + p.deltaMs];
    void saveBlockPlan.save({
      method: 'patch',
      url: `/cars/${p.bar.car_id}/blocks/${p.bar.id}`,
      body: { start_at: new Date(start).toISOString(), end_at: new Date(end).toISOString() },
    });
  };
  /**
   * 316 (US5, decision A) — an unpaid booking's new length, priced by `reprice-preview` (the save's
   * own path: the exact total it would write, or its refusal). Bound to its period like every plan.
   */
  const [resizePlan, setResizePlan] = useState<{
    proposal: ResizeProposal;
    anchorRect: DOMRect;
    key: string;
  } | null>(null);
  const liveResize = resizePlan && resizePlan.key === periodKey ? resizePlan : null;
  const [resizeError, setResizeError] = useState<string | null>(null);
  const closeResize = () => {
    setResizePlan(null);
    setResizeError(null);
  };
  const resizeWindow = liveResize
    ? {
        pickup_at: new Date(liveResize.proposal.start).toISOString(),
        return_at: new Date(liveResize.proposal.end).toISOString(),
      }
    : null;
  const resizePreview = useQuery({
    queryKey: ['schedule-reprice-preview', liveResize?.proposal.bar.id, resizeWindow],
    queryFn: () =>
      api.get<{
        base_price_minor: number;
        total_price_minor: number;
        current_total_minor: number;
        currency: string;
      }>(
        `/bookings/${liveResize?.proposal.bar.booking_id}/reprice-preview?${new URLSearchParams(
          resizeWindow ?? {},
        ).toString()}`,
      ),
    enabled: Boolean(liveResize && resizeWindow),
    retry: false,
  });
  const money = (minor: number, currency: string) =>
    `${currency === 'MYR' ? 'RM' : `${currency} `}${(minor / 100).toFixed(2)}`;
  const resizePrice: ResizePrice = resizePreview.error
    ? { state: 'refused', message: (resizePreview.error as Error).message }
    : resizePreview.data
      ? {
          state: 'ok',
          newLabel: money(resizePreview.data.total_price_minor, resizePreview.data.currency),
          currentLabel: money(resizePreview.data.current_total_minor, resizePreview.data.currency),
        }
      : { state: 'loading' };
  // 386 (#1278, FR-011): warn — never refuse — when a move or a new length leaves less than the car's gap.
  const moveGapInput = (() => {
    if (!liveMove || liveMove.proposal.kind === 'block-move') return null;
    const p = liveMove.proposal;
    const shift = p.kind === 'move' ? p.deltaMs : 0;
    const span = !movePreviewLoading ? movePreview?.span : null;
    return {
      carId: p.targetCarId,
      pickupAt: span?.to.pickup_at ?? new Date(Date.parse(p.bar.start_at) + shift).toISOString(),
      returnAt: span?.to.return_at ?? new Date(Date.parse(p.bar.end_at) + shift).toISOString(),
      bookingId: p.bar.booking_id,
    };
  })();
  const moveGap = useGapWarning(moveGapInput ?? { carId: null, pickupAt: null, returnAt: null });
  const resizeGap = useGapWarning(
    liveResize && resizeWindow
      ? {
          carId: liveResize.proposal.bar.car_id,
          pickupAt: resizeWindow.pickup_at,
          returnAt: resizeWindow.return_at,
          bookingId: liveResize.proposal.bar.booking_id,
        }
      : { carId: null, pickupAt: null, returnAt: null },
  );
  const saveResize = useSaveMutation<{ pickup_at: string; return_at: string; reprice: true }>({
    resource: 'booking',
    affects: ['schedule'],
    mutationFn: (body) =>
      api.patch(`/bookings/${liveResize?.proposal.bar.booking_id}/schedule`, body),
    onSaved: closeResize,
    onError: (err) => setResizeError((err as Error).message || 'The length could not be changed.'),
  });

  const planBlock = (proposal: NonNullable<typeof blockPlan>['proposal'], anchorRect: DOMRect) => {
    if (!periodKey) return;
    setBlockPlanError(null);
    setBlockPlan({ proposal, anchorRect, key: periodKey });
  };

  // 329 (FR-004): the zone and counts line, in the grid's corner; above the empty message when there
  // is no grid, so it always shows.
  // Two lines, because the car column is narrow (176 px at 1440): the zone, then the counts. "Times shown
  // in" does not fit beside the zone there, so it is read out (and in the tooltip) but not drawn.
  const zone = data?.window.tz ?? 'Asia/Kuala_Lumpur';
  const zoneText = `Times shown in ${zone}`;
  const countsText = data ? ` · ${data.counts.cars} cars · ${data.counts.bars} periods` : '';
  const zoneLine = (
    <p
      data-testid="schedule-zone"
      title={`${zoneText}${countsText}`}
      className="m-0 min-w-0 text-[10px] leading-[13px] text-steel"
    >
      <span className="block truncate">
        <span className="sr-only">Times shown in </span>
        {zone}
      </span>
      {countsText && <span className="block truncate">{countsText}</span>}
    </p>
  );

  return (
    <div>
      <PageHeader
        title="Schedule"
        description="Fleet occupancy by day, week or month — every car, every booking and block."
      />

      {/* 141 FR-009: bounded, shadowed card — the same treatment every other list page's
          filter row already sits inside (spec 113), which this day-nav/zone-line area never
          got when it shipped separately (094/096). */}
      <Card padding="sm" className="mb-3">
        {/* 316 (US6, FR-020): one compact toolbar. Everything it changes is written to the address. */}
        <ScheduleToolbar
          view={view}
          date={data?.window.day ?? day ?? ''}
          disabled={!data}
          plate={plateInput}
          status={statuses[0] ?? ''}
          location={locationId ?? ''}
          locations={(locationList?.items ?? []).map((l) => ({ id: l._id, name: l.name }))}
          unavailable={showUnavailable}
          onView={(v) => goTo(v, anchorDay)}
          onStep={(direction) => data && goTo(view, stepDate(view, data.window.day, direction))}
          onToday={() => goTo(view, mytDateOf(Date.now()))}
          onDate={(d) => goTo(view, d)}
          onPlate={setPlateInput}
          onStatus={(st) => setParams({ statuses: st ? [st] : [] })}
          onLocation={(l) => setParams({ location: l || null })}
          onUnavailable={(on) => setParams({ unavailable: on })}
          freeFor={
            <FreeForControl
              day={data?.window.day ?? day ?? new Date().toISOString().slice(0, 10)}
              from={freeFrom}
              to={freeTo}
              onApply={(from, to) => setParams({ freeFrom: from, freeTo: to })}
              onClear={() => setParams({ freeFrom: null, freeTo: null })}
            />
          }
          displayMenu={
            <DisplayMenu
              groupMode={groupMode}
              onGroup={(mode) => setParams({ group: mode })}
              density={density}
              onDensity={chooseDensity}
              canReorder={canReorderCars && Boolean(data && data.cars.length > 1)}
              onReorder={() => setReordering(true)}
            />
          }
          guideOpen={guideOpen}
          onGuide={() => chooseGuide(!guideOpen)}
          guideButtonRef={guideButton}
        />
      </Card>

      {/* 329 (FR-006): the Guide sits over the right of what follows, taking no space of its own. */}
      <div className="relative">
        {guideOpen && (
          <ScheduleGuide
            abilities={{ canMoveBooking, canEditBlocks: canEdit }}
            view={view}
            onClose={() => chooseGuide(false)}
            returnFocusTo={guideButton}
            className="fixed inset-0 z-50 sm:absolute sm:inset-auto sm:top-0 sm:right-0 sm:z-30 sm:h-[70vh] sm:min-h-[420px] sm:w-[420px] sm:rounded-lg"
          />
        )}
        {!(data && data.cars.length > 0) && <div className="mb-2">{zoneLine}</div>}

        {/* 320 (FR-008): a refused free-for window says why, in its own place. */}
        {error && freeActive && (
          <p
            data-testid="schedule-free-for-error"
            role="alert"
            className="rounded border border-hairline bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
          >
            {(error as Error).message}
          </p>
        )}
        {((error && !freeActive) || writeError) && (
          <p
            data-testid="schedule-error"
            className="rounded border border-hairline bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
          >
            {writeError ?? (error as Error | null)?.message}
          </p>
        )}

        {isLoading && (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        )}

        {/* 301 (#437) US1 FR-013: an empty grid is ambiguous between "nothing scheduled" and
          "everything is hidden by the filter" — state it plainly rather than leave the operator
          guessing, and only when the filter itself could be the reason (it is currently off). */}
        {data && data.cars.length === 0 && !showUnavailable && !freeActive && (
          <p
            data-testid="schedule-empty"
            className="rounded border border-hairline bg-surface px-3 py-6 text-center text-sm text-steel"
          >
            No cars to show for this day. Every car in scope may be hidden by the "Show unavailable
            cars" filter above — turn it on to check.
          </p>
        )}
        {data && data.cars.length === 0 && showUnavailable && !freeActive && (
          <p
            data-testid="schedule-empty"
            className="rounded border border-hairline bg-surface px-3 py-6 text-center text-sm text-steel"
          >
            No cars to show for this day.
          </p>
        )}

        {/* 320 (FR-007): the window and how many cars are free for it, or that none is. */}
        {data && freeActive && freeFrom && freeTo && (
          <div
            data-testid="schedule-free-for-banner"
            className="flex flex-wrap items-center justify-between gap-2 rounded border border-hairline bg-surface-feature px-3 py-2 text-sm text-ink"
          >
            <span>
              {data.cars.length === 0
                ? `No car is free for ${freeForLabel(freeFrom, freeTo)}.`
                : `${data.cars.length} ${data.cars.length === 1 ? 'car' : 'cars'} free for ${freeForLabel(freeFrom, freeTo)}`}
            </span>
            <button
              type="button"
              onClick={() => setParams({ freeFrom: null, freeTo: null })}
              className="text-xs text-brand-blue hover:underline"
            >
              Clear
            </button>
          </div>
        )}
        {/* 317 (FR-013): the figures count every status; say so while the bars are filtered. */}
        {data && data.cars.length > 0 && statuses.length > 0 && (
          <p data-testid="schedule-occupancy-note" className="-mt-2 mb-3 text-xs text-steel">
            Figures include every status.
          </p>
        )}

        {/* 096 (FR-012): a day change fades rather than cutting. Driven by the query's own fetching
          state, so it is real feedback and not decoration, and behind `motion-safe:` so an
          operator who asked for reduced motion gets an instant swap instead. Opacity only —
          nothing here delays or blocks interaction (FR-013). */}
        {data && data.cars.length > 0 && (
          <div
            data-testid="schedule-grid"
            data-busy={String(isFetching)}
            className={[
              'motion-safe:transition-opacity motion-safe:duration-200',
              isFetching ? 'opacity-60' : 'opacity-100',
            ].join(' ')}
          >
            {/* 316 (FR-021): the grid scrolls inside itself, both ways; the page never scrolls
              sideways. The axis and the car column stay pinned. */}
            <div data-testid="schedule-scroll" className="max-h-[70vh] overflow-auto rounded-lg">
              <ScheduleTimeline
                view={view}
                range={{ start: Date.parse(data.window.from), end: Date.parse(data.window.to) }}
                cars={data.cars}
                bars={visibleBars}
                allBars={data.bars}
                occupancy={occupancy}
                density={density}
                corner={zoneLine}
                grouping={
                  groups && groupMode !== 'none'
                    ? {
                        mode: groupMode,
                        groups,
                        collapsed: new Set(collapsed[groupMode]),
                        onToggle: toggleGroup,
                      }
                    : undefined
                }
                now={Date.now()}
                onOpenBar={openBar}
                // 368 (#1236, FR-004): a day cell's "Open day" or a day heading — that date in the Day view.
                onOpenDay={(date) => goTo('day', date)}
                abilities={{ canMoveBooking, canEditBlocks: canEdit }}
                onProposeMove={(proposal, anchorRect) => {
                  if (!periodKey) return;
                  if (proposal.kind === 'block-move') {
                    planBlock(proposal, anchorRect);
                    return;
                  }
                  setMoveError(null);
                  setMoveAlternatives([]);
                  setMove({ proposal, anchorRect, key: periodKey });
                }}
                onProposeResize={(proposal, anchorRect) => {
                  if (proposal.bar.kind !== 'booking' && proposal.bar.kind !== 'extension') {
                    planBlock(proposal, anchorRect);
                    return;
                  }
                  if (!periodKey) return;
                  setResizeError(null);
                  setResizePlan({ proposal, anchorRect, key: periodKey });
                }}
                onProposeCreate={(proposal, anchorRect) => {
                  // Day keeps 096's editable popover (its time fields work within one day); Week
                  // and Month confirm the drawn range in BlockChangePopover (it can span days).
                  if (view === 'day') {
                    proposalDay.current = data.window.day;
                    setProposalError(null);
                    setProposal({
                      carId: proposal.carId,
                      startAt: new Date(proposal.start),
                      endAt: new Date(proposal.end),
                      type: 'manual_block',
                      reason: '',
                      anchorRect,
                    });
                    return;
                  }
                  planBlock(proposal, anchorRect);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 096: nothing is written while this is open. Portalled to document level from inside the
          component, so its position is unaffected by the grid's scroll container. */}
      {liveProposal && data && (
        <CreateBlockPopover
          proposal={liveProposal}
          carLabel={data.cars.find((c) => c.id === liveProposal.carId)?.label ?? 'this car'}
          windowFrom={new Date(data.window.from)}
          error={proposalError}
          pending={createBlock.isPending}
          onConfirm={(confirmed) => createBlock.mutate(confirmed)}
          onDismiss={() => {
            setProposal(null);
            setProposalError(null);
          }}
        />
      )}

      {reordering && data && (
        <CarReorderPanel
          cars={data.cars}
          pending={reorderCar.isPending}
          onMove={(carId, afterCarId) => reorderCar.mutate({ carId, afterCarId })}
          onClose={() => setReordering(false)}
        />
      )}

      {/* #631: a booking's details, beside it, with the way to its page. */}
      {bookingSelection && data && (
        <BookingDetailsPopover
          key={bookingSelection.bar.id}
          bar={bookingSelection.bar}
          carLabel={carName(bookingSelection.bar.car_id)}
          anchorRect={bookingSelection.anchorRect}
          onOpenBooking={(bookingId) => navigate(`/bookings/${bookingId}`)}
          onDismiss={() => setSelected(null)}
        />
      )}

      {/* 316 (FR-015): a block's details, beside it — nothing is inserted above the grid. */}
      {editableSelection && data && (
        <BlockDetailsPopover
          key={editableSelection.bar.id}
          bar={editableSelection.bar}
          carLabel={carName(editableSelection.bar.car_id)}
          anchorRect={editableSelection.anchorRect}
          canRemove={canRemove}
          onSaveReason={(reason) =>
            patchBlock.mutate({
              carId: barCar(editableSelection.bar),
              blockId: editableSelection.bar.id,
              body: { reason },
            })
          }
          onRemove={() =>
            removeBlock.mutate({
              carId: barCar(editableSelection.bar),
              blockId: editableSelection.bar.id,
            })
          }
          onDismiss={() => setSelected(null)}
        />
      )}

      {/* 316 (US5): an unpaid booking's new length, with the price the save would write. */}
      {liveResize && data && resizeWindow && (
        <ResizeBookingPopover
          summary={{
            item: `Booking ${shortRef(liveResize.proposal.bar.booking_id ?? liveResize.proposal.bar.id)}`,
            from: mytPeriod(
              Date.parse(liveResize.proposal.bar.start_at),
              Date.parse(liveResize.proposal.bar.end_at),
            ),
            to: mytPeriod(liveResize.proposal.start, liveResize.proposal.end),
          }}
          price={resizePrice}
          anchorRect={liveResize.anchorRect}
          error={resizeError}
          pending={saveResize.isPending}
          gapWarning={resizeGap}
          onConfirm={() => void saveResize.save({ ...resizeWindow, reprice: true })}
          onDismiss={closeResize}
        />
      )}

      {/* 316 (US4): a block moved, resized or drawn on the new timeline. */}
      {liveBlockPlan && data && (
        <BlockChangePopover
          mode={
            liveBlockPlan.proposal.kind === 'create'
              ? 'create'
              : liveBlockPlan.proposal.kind === 'resize'
                ? 'resize'
                : 'move'
          }
          carLabel={carName(
            liveBlockPlan.proposal.kind === 'create'
              ? liveBlockPlan.proposal.carId
              : liveBlockPlan.proposal.bar.car_id,
          )}
          {...(() => {
            const p = liveBlockPlan.proposal;
            if (p.kind === 'create') return { to: mytPeriod(p.start, p.end) };
            const start = Date.parse(p.bar.start_at);
            const end = Date.parse(p.bar.end_at);
            return {
              kindLabel: barStyleFor(p.bar).word,
              from: mytPeriod(start, end),
              to:
                p.kind === 'resize'
                  ? mytPeriod(p.start, p.end)
                  : mytPeriod(start + p.deltaMs, end + p.deltaMs),
            };
          })()}
          anchorRect={liveBlockPlan.anchorRect}
          error={blockPlanError}
          pending={saveBlockPlan.isPending}
          onConfirm={confirmBlockPlan}
          onDismiss={closeBlockPlan}
        />
      )}

      {/* 316 (US1): a booking dragged on the new timeline. One popover, three modes. */}
      {liveMove && data && liveMove.proposal.kind !== 'block-move' && (
        <MoveBookingPopover
          mode={
            liveMove.proposal.kind === 'move'
              ? 'move'
              : rentalPaid(liveMove.proposal.bar)
                ? 'car-paid'
                : 'car'
          }
          carLabel={carName(liveMove.proposal.targetCarId)}
          move={
            liveMove.proposal.kind === 'move'
              ? {
                  ...moveStateOf(movePreviewLoading ? undefined : movePreview),
                  clash: shownClash
                    ? {
                        message: shownClash.message,
                        suggestionLabel: shownClash.suggestion
                          ? mytPeriod(
                              Date.parse(shownClash.suggestion.pickup_at),
                              Date.parse(shownClash.suggestion.return_at),
                            )
                          : null,
                      }
                    : null,
                }
              : undefined
          }
          onUseSuggestion={useSuggestion}
          summary={(() => {
            const p = liveMove.proposal;
            const item = `Booking ${shortRef(p.bar.booking_id ?? p.bar.id)}`;
            const start = Date.parse(p.bar.start_at);
            const end = Date.parse(p.bar.end_at);
            if (p.kind === 'car') {
              return {
                item,
                from: carName(p.bar.car_id),
                to: carName(p.targetCarId),
                when: mytPeriod(start, end),
              };
            }
            // 328: the server's span when the preview is in (a whole rental), else the bar's own.
            const span = !movePreviewLoading ? movePreview?.span : null;
            const extensions = (!movePreviewLoading ? (movePreview?.legs.length ?? 1) : 1) - 1;
            const fromStart = span ? Date.parse(span.from.pickup_at) : start;
            const fromEnd = span ? Date.parse(span.from.return_at) : end;
            const toStart = span ? Date.parse(span.to.pickup_at) : start + p.deltaMs;
            const toEnd = span ? Date.parse(span.to.return_at) : end + p.deltaMs;
            return {
              // US3: say that the whole rental moves, not only the leg that was dragged.
              item:
                extensions > 0
                  ? `${item} and its ${extensions === 1 ? 'extension' : `${extensions} extensions`} (the whole rental moves)`
                  : item,
              from: `${carName(p.bar.car_id)}, ${mytPeriod(fromStart, fromEnd)}`,
              to: `${carName(p.targetCarId)}, ${mytPeriod(toStart, toEnd)}`,
            };
          })()}
          anchorRect={liveMove.anchorRect}
          priceLabel={moveError ? undefined : moveQuoteLabel}
          error={moveError}
          alternatives={moveAlternatives}
          pending={saveMove.isPending}
          gapWarning={moveGap}
          onConfirm={confirmMove}
          onDismiss={closeMove}
        />
      )}
    </div>
  );
}

/** `window.localStorage` itself throws in some locked-down browsers; absent then means "not kept". */
function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
